import React, { useState, useEffect } from 'react';
import { tradingApi, Order, Position } from '../services/api';
import { Briefcase, Tag as TagIcon, X } from 'lucide-react';
import OpenOrders from './OpenOrders';
import FilledOrders from './FilledOrders';
import { formatTimeET } from '../utils/datetime';
import { useOpenOrdersQuery, usePositionsQuery } from '../store/tradingApiSlice';
import { useQuoteConnectionStatus, useQuotes } from '../market/QuoteStore';
import ModalPortal from './ModalPortal';
import PositionChartModal from './PositionChartModal';
import ManualOrderEntry, { ManualOrderPrefill } from './ManualOrderEntry';
import { PositionStrategyTagService } from './positionStrategyTagService';
import PositionStrategyTagModals from './PositionStrategyTagModals';
import OpenPositionsTable from './OpenPositionsTable';
import { usePositionStrategyTags } from './usePositionStrategyTags';
import {
  addLiveQuoteToPosition,
  filterPositions,
  getPositionDisplaySymbol,
  getPositionQuoteIdentifiers,
  getPositionStrategyNameOptions,
  getPositionStrategyOptions,
  getSecurityType,
  securityTypeOptions,
  type SecurityTypeFilter,
} from './positionsOrdersUtils';

interface PositionsOrdersPanelProps {
  refreshInterval?: number;
  onEditOrder?: (order: Order) => void;
  className?: string;
}

interface PendingCloseOrder {
  symbol: string;
  displaySymbol: string;
  quantity: number;
  side: 'BUY' | 'SELL';
  secType: string;
  contract: Partial<Position>;
}

const PositionsOrdersPanel: React.FC<PositionsOrdersPanelProps> = ({ refreshInterval = 30000, onEditOrder, className = '' }) => {
  const positionTagService = React.useMemo(
    () => new PositionStrategyTagService(tradingApi),
    [],
  );
  const {
    data: positions = null,
    isLoading: loading,
    isFetching: refreshing,
    error: positionsError,
    refetch: refetchPositions,
  } = usePositionsQuery(undefined, {
    pollingInterval: refreshInterval,
    skipPollingIfUnfocused: true,
  });
  const { data: ordersData, refetch: refetchOrders } = useOpenOrdersQuery(
    { page: 1, pageSize: 100 },
    {
      pollingInterval: refreshInterval,
      skipPollingIfUnfocused: true,
    },
  );
  const [actionError, setActionError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'positions' | 'orders'>('positions');
  const [strategyFilter, setStrategyFilter] = useState('ALL');
  const [securityTypeFilter, setSecurityTypeFilter] = useState<SecurityTypeFilter>('ALL');
  const [pendingClose, setPendingClose] = useState<PendingCloseOrder | null>(null);
  const [chartPosition, setChartPosition] = useState<Position | null>(null);
  const tagManagement = usePositionStrategyTags({
    service: positionTagService,
    refreshAfterDelete: () => refetchPositions(),
    refreshAfterSave: () => refetchPositions().unwrap(),
  });
  const { openManager: openStrategyTagManager, openPositionTagEditor } = tagManagement;
  const lastUpdated = positions?.timestamp ? formatTimeET(positions.timestamp) : null;
  const openOrdersCount = (ordersData?.data?.orders || []).filter((order) =>
    securityTypeFilter === 'ALL' || getSecurityType(order) === securityTypeFilter,
  ).length;
  const error = actionError || (positionsError && !positions ? 'Failed to fetch positions' : null);
  const positionsStale = !!positions?.data?.stale;
  const positionsSnapshotAvailable = positions?.data?.snapshot_available !== false;
  const positionsInitializing = positions?.data?.connection_status === 'initializing';
  const ordersInitializing = ordersData?.data?.connection_status === 'initializing';
  const quoteIdentifiers = getPositionQuoteIdentifiers(positions?.data?.positions || []);
  const liveQuotes = useQuotes(quoteIdentifiers);
  const quoteConnectionStatus = useQuoteConnectionStatus();

  const displayedPositions = (positions?.data?.positions || []).map((position, index) => {
    const identifier = String(quoteIdentifiers[index] || '').toUpperCase();
    return addLiveQuoteToPosition(
      position,
      liveQuotes[identifier],
      quoteConnectionStatus,
      positionsStale,
    );
  });
  const strategyOptions = getPositionStrategyOptions(displayedPositions);
  const strategyNameOptions = getPositionStrategyNameOptions(displayedPositions);
  const filteredPositions = filterPositions(displayedPositions, strategyFilter, securityTypeFilter);

  const fetchPositions = React.useCallback(async () => {
    setActionError(null);
    try {
      await Promise.all([refetchPositions().unwrap(), refetchOrders().unwrap()]);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to fetch positions');
    }
  }, [refetchOrders, refetchPositions]);

  useEffect(() => {
    const handleRefresh = () => fetchPositions();
    window.addEventListener('trading-data-refresh', handleRefresh);
    return () => window.removeEventListener('trading-data-refresh', handleRefresh);
  }, [fetchPositions]);

  useEffect(() => {
    if (!positionsInitializing && !ordersInitializing) return;
    const retryId = window.setTimeout(() => fetchPositions(), 2000);
    return () => window.clearTimeout(retryId);
  }, [fetchPositions, ordersInitializing, positions?.timestamp, positionsInitializing]);

  const accountCurrency = positions?.data?.currency || 'USD';

  const handleCloseClick = (position: Position) => {
    if (!position.symbol || position.quantity === undefined) return;

    // Calculate the closing order details
    const qty = position.quantity;
    const side: 'BUY' | 'SELL' = qty < 0 ? 'BUY' : 'SELL';

    setPendingClose({
      symbol: position.symbol,
      displaySymbol: getPositionDisplaySymbol(position),
      quantity: Math.abs(qty),
      side,
      secType: position.secType || 'STK',
      contract: {
        con_id: position.con_id,
        exchange: position.exchange,
        currency: position.currency,
        local_symbol: position.local_symbol,
        trading_class: position.trading_class,
        last_trade_date_or_contract_month: position.last_trade_date_or_contract_month,
        multiplier: position.multiplier,
      },
    });
  };

  const handleCancelClose = () => {
    setPendingClose(null);
  };

  const pendingCloseDraft = React.useMemo<ManualOrderPrefill | null>(() => {
    if (!pendingClose) return null;

    return {
      symbol: pendingClose.symbol,
      rootSymbol: pendingClose.symbol,
      side: pendingClose.side,
      quantity: pendingClose.quantity,
      order_type: 'MARKET',
      secType: pendingClose.secType,
      strategy: 'MANUAL',
      tif: 'DAY',
      outside_rth: false,
      con_id: pendingClose.contract.con_id,
      exchange: pendingClose.contract.exchange,
      currency: pendingClose.contract.currency,
      localSymbol: pendingClose.contract.local_symbol,
      tradingClass: pendingClose.contract.trading_class,
      lastTradeDateOrContractMonth: pendingClose.contract.last_trade_date_or_contract_month,
      multiplier: pendingClose.contract.multiplier,
    };
  }, [pendingClose]);

  const handleCloseOrderSubmitted = () => {
    setPendingClose(null);
    setActionError(null);
    void fetchPositions();
  };


  return (
    <div className={`panel full-bleed-panel flex min-h-0 w-full flex-col ${className}`}>
      <div className="flex shrink-0 flex-col justify-between gap-4 border-b border-white/10 px-6 py-5 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-neon/10 text-neon">
            <Briefcase className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white tracking-tight">Positions & Orders</h2>
            <p className="text-sm text-muted">Real-time portfolio oversight and execution logs.</p>
          </div>
        </div>
        <div className="flex items-center p-1 bg-white/5 border border-white/10 rounded-xl">
          <button
            onClick={() => setActiveTab('positions')}
            className={`px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all duration-300 flex items-center gap-2 ${activeTab === 'positions'
              ? 'bg-neon/10 text-neon shadow-[0_0_15px_rgba(45,212,191,0.1)]'
              : 'text-muted hover:text-white hover:bg-white/5'
              }`}
          >
            <span>Positions</span>
            {filteredPositions.length > 0 && (
              <span className={`flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[9px] font-black leading-none ${
                activeTab === 'positions' ? 'bg-neon text-black shadow-[0_0_10px_rgba(var(--neon-rgb),0.4)]' : 'bg-white/10 text-white/60 border border-white/10'
              }`}>
                {filteredPositions.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all duration-300 flex items-center gap-2 ${activeTab === 'orders'
              ? 'bg-neon/10 text-neon shadow-[0_0_15px_rgba(45,212,191,0.1)]'
              : 'text-muted hover:text-white hover:bg-white/5'
              }`}
          >
            <span>Orders</span>
            {openOrdersCount > 0 && (
              <span className={`flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[9px] font-black leading-none ${
                activeTab === 'orders' ? 'bg-neon text-black shadow-[0_0_10px_rgba(var(--neon-rgb),0.4)]' : 'bg-white/10 text-white/60 border border-white/10'
              }`}>
                {openOrdersCount}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col p-6">
        {!actionError && (positions?.success || ordersData?.success) && (
          <div className="mb-6 flex flex-wrap items-center gap-x-2 gap-y-2">
            {activeTab === 'positions' && (
              <>
                <button
                  type="button"
                  onClick={openStrategyTagManager}
                  className="mr-2 inline-flex items-center gap-1.5 rounded-full border border-neon/25 bg-neon/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-neon transition-colors hover:border-neon/50 hover:bg-neon/15"
                >
                  <TagIcon className="h-3 w-3" />
                  Manage tags
                </button>
                <span className="mr-1 text-[10px] font-bold uppercase tracking-wider text-muted">Strategy</span>
                {['ALL', ...strategyOptions].map((strategy) => (
                  <button
                    key={strategy}
                    type="button"
                    onClick={() => setStrategyFilter(strategy)}
                    className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${strategyFilter === strategy
                      ? 'border-neon/40 bg-neon/15 text-neon'
                      : 'border-white/10 bg-white/5 text-white/55 hover:border-white/20 hover:text-white'
                      }`}
                  >
                    {strategy === 'ALL' ? 'All strategies' : strategy}
                  </button>
                ))}
                <span className="mx-1 hidden h-4 w-px bg-white/10 sm:block" />
              </>
            )}
            <span className="mr-1 text-[10px] font-bold uppercase tracking-wider text-muted">Asset</span>
            {securityTypeOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setSecurityTypeFilter(option.value)}
                className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${securityTypeFilter === option.value
                  ? 'border-neon/40 bg-neon/15 text-neon'
                  : 'border-white/10 bg-white/5 text-white/55 hover:border-white/20 hover:text-white'
                  }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
        {activeTab === 'positions' ? (
          <OpenPositionsTable
            positions={filteredPositions}
            hasAnyPositions={displayedPositions.length > 0}
            positionsSucceeded={positions?.success === true}
            loading={loading}
            refreshing={refreshing}
            error={error}
            lastUpdated={lastUpdated}
            accountCurrency={accountCurrency}
            stale={positionsStale}
            snapshotAvailable={positionsSnapshotAvailable}
            initializing={positionsInitializing}
            connectionUnavailable={positions?.data?.connection_status === 'unavailable'}
            onRetry={() => void fetchPositions()}
            onClosePosition={handleCloseClick}
            onOpenPositionChart={setChartPosition}
            onEditPositionTags={openPositionTagEditor}
          />
        ) : (
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
            <OpenOrders
              key={`open-orders-${securityTypeFilter}`}
              refreshInterval={refreshInterval}
              onEditOrder={onEditOrder}
              securityTypeFilter={securityTypeFilter}
            />
            <FilledOrders
              key={`filled-orders-${securityTypeFilter}`}
              refreshInterval={refreshInterval}
              securityTypeFilter={securityTypeFilter}
            />
          </div>
        )}
      </div>

      {chartPosition && (
        <PositionChartModal
          position={chartPosition}
          onClose={() => setChartPosition(null)}
        />
      )}

      <PositionStrategyTagModals state={tagManagement} strategyNameOptions={strategyNameOptions} />

      {/* Closing orders use the same editable manual-order form as the dashboard order entry. */}
      {pendingCloseDraft && (
        <ModalPortal>
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/55 p-0 backdrop-blur-sm animate-in fade-in duration-300 sm:p-4"
            onClick={(event) => {
              if (event.target === event.currentTarget) handleCancelClose();
            }}
            role="dialog"
            aria-modal="true"
            aria-label={`Close ${pendingCloseDraft.symbol}`}
          >
            <div className="relative max-h-[100dvh] w-full max-w-2xl overflow-y-auto animate-in zoom-in duration-300 sm:max-h-[calc(100vh-2rem)]">
              <button
                type="button"
                onClick={handleCancelClose}
                aria-label="Close order modal"
                title="Close"
                className="absolute right-3 top-3 z-10 rounded-lg p-2 text-white/50 transition-colors hover:bg-white/10 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
              <ManualOrderEntry
                prefillOrder={pendingCloseDraft}
                inModal
                showCancelButton
                onCancel={handleCancelClose}
                onOrderSubmitted={handleCloseOrderSubmitted}
              />
            </div>
          </div>
        </ModalPortal>
      )}
    </div>
  );
};

export default PositionsOrdersPanel;
